import { type StateCreator, create } from "zustand";
import { devtools /*persist*/ } from "zustand/middleware";
import {
	EventFilterCondition,
	EventFilterValue,
	GPEventResponse,
	GamePageResponse,
	GamePageLazyResponse,
	Interval,
	MergedGpEventResponse,
	VideoSidebar,
	FilterCategory,
	ProcessingLevelEnum,
} from "./types";
import EventFilter from "./components/Video/Filter/eventFilter";
import { createIntervalsFromMergedEvents, mergeCloseTimeSpanEvents, excludeDirectionalEvents } from "./functions";
import { useStore as useTimeStore } from "./time-store";
import { useStore as useEditStore } from "./edit-store";
import { isAttachModeActive } from "./attach-mode-active";

function syncSelectedEventsAfterFilterChange(mergedEvents: MergedGpEventResponse[]) {
	// Attach mode accumulates clips across filter changes — keep the selection.
	if (isAttachModeActive()) {
		return;
	}

	const editState = useEditStore.getState();

	if (editState.showClipCreateModal) {
		editState.setSelectedEvents(mergedEvents);
		return;
	}

	editState.setSelectedEvents([]);
}

interface GameState {
	eventFilter: EventFilter;
	gameData: GamePageResponse | null | undefined;
	gameDataLazy: GamePageLazyResponse | null | undefined;
	filteredFilterValues: EventFilterValue[];
	filterValues: EventFilterValue[];
	lastNonCommentFilterValues: EventFilterValue[];
	sidebar: VideoSidebar | null;
	sidebarCommentEventId: number | null;
	periods: string[];

	events: GPEventResponse[];
	mergedEvents: MergedGpEventResponse[];
	intervals: Interval[];
	jumpBetweenIntervals: boolean;
	followVideo: boolean;
	isFullscreen: boolean;
	setGameData: (gameData: GamePageResponse, gameDataLazy?: GamePageLazyResponse) => void;
	setFullscreen: (isFullscreen: boolean) => void;
	setFilterValues: (filterValues: EventFilterValue[]) => void;
	setFilteredFilterValues: (filteredFilterValues: EventFilterValue[]) => void;
	setSidebar: (sidebar: VideoSidebar | null) => void;
	setSidebarAndFilter: (sidebar: VideoSidebar) => void;
	setSidebarCommentEventId: (eventId: number | null) => void;
	setEvents: (events: GPEventResponse[], activeFilterValues?: EventFilterValue[]) => void;
	setFollowVideo: (followVideo: boolean) => void;
	setMergedEvents: (mergedEvents: MergedGpEventResponse[]) => void;
}

type Store = StateCreator<GameState>;

function filterEvents(eventFilter: EventFilter, gameData: GamePageResponse, filteredFilterValues: EventFilterValue[]) {
	let events: GPEventResponse[];
	if (!gameData.permissions.showEvents) {
		events = eventFilter.filterByConditions([{ type: "type", value: ["highlight"] } as EventFilterCondition], eventFilter.events);
	} else {
		// Empty filters → all events (same path as EventFilter with no team/player/period selection).
		events = eventFilter.filter(filteredFilterValues);
	}

	// For Limited processing games, exclude directional events to match the count displayed in stats
	if (gameData?.game?.processingLevel === ProcessingLevelEnum.Limited) {
		events = excludeDirectionalEvents(events);
	}

	return events;
}

/** Jump only with an active filter — default (unfiltered) list plays continuously. */
function shouldJumpBetweenIntervals(
	mergedEvents: MergedGpEventResponse[],
	activeFilterValues: EventFilterValue[],
	sidebar: VideoSidebar | null,
): boolean {
	return mergedEvents.length !== 0 && activeFilterValues.length !== 0 && sidebar !== VideoSidebar.Timeline;
}

function resolveActiveFilterValuesForSidebar(
	filterValues: EventFilterValue[],
	filteredFilterValues: EventFilterValue[],
): EventFilterValue[] {
	if (filterValues.length !== 0) {
		return filterValues;
	}

	return filteredFilterValues;
}

const store: Store = (set) => ({
	eventFilter: null as unknown as EventFilter,
	gameData: null,
	gameDataLazy: null,
	filteredFilterValues: [],
	filterValues: [],
	lastNonCommentFilterValues: [],
	sidebar: VideoSidebar.TeamSheet,
	sidebarCommentEventId: null,
	events: [],
	mergedEvents: [],
	intervals: [],
	jumpBetweenIntervals: false,
	followVideo: false,
	periods: [],
	isFullscreen: false,
	setFullscreen: (isFullscreen) => set((state) => ({ ...state, isFullscreen })),
	setGameData: (gameData, gameDataLazy) => {
		set((state) => {
			const eventFilter = gameDataLazy ? new EventFilter(gameData, gameDataLazy) : new EventFilter(gameData);
			const events = gameDataLazy ? filterEvents(eventFilter, gameData, state.filteredFilterValues) : [];
			const mergedEvents = mergeCloseTimeSpanEvents(events);
			const intervals = createIntervalsFromMergedEvents(mergedEvents);
			const jumpBetweenIntervals = shouldJumpBetweenIntervals(
				mergedEvents,
				state.filteredFilterValues,
				state.sidebar,
			);
			const periods = eventFilter.getFilterViewsByCategories([FilterCategory.Period]).map((fw) => fw.value);
			return {
				...state,
				gameData,
				gameDataLazy,
				events,
				eventFilter,
				mergedEvents,
				intervals,
				jumpBetweenIntervals,
				periods,
			};
		});
	},
	setFilteredFilterValues(filteredFilterValues) {
		set((state) => {
			const jumpBetweenIntervals = shouldJumpBetweenIntervals(
				state.mergedEvents,
				filteredFilterValues,
				state.sidebar,
			);
			return { ...state, filteredFilterValues, jumpBetweenIntervals };
		});
	},
	setFilterValues(filterValues) {
		set((state) => {
			const events = filterEvents(state.eventFilter, state.gameData!, filterValues);
			const mergedEvents = mergeCloseTimeSpanEvents(events);
			const intervals = createIntervalsFromMergedEvents(mergedEvents);
			const filteredFilterValues = filterValues.length === 0 ? [] : state.filteredFilterValues;
			const jumpBetweenIntervals = shouldJumpBetweenIntervals(
				mergedEvents,
				filterValues,
				state.sidebar,
			);

			syncSelectedEventsAfterFilterChange(mergedEvents);

			useTimeStore.setState((state) => {
				return { ...state, currentTime: 0 };
			});

			const lastNonCommentFilterValues = state.sidebar === VideoSidebar.EventsWithComments ? state.lastNonCommentFilterValues : filterValues;

			return { ...state, filterValues, filteredFilterValues, events, mergedEvents, intervals, jumpBetweenIntervals, lastNonCommentFilterValues };
		});
	},
	setSidebar(sidebar) {
		set((state) => {
			const clearCommentPanel =
				sidebar === null ||
				(sidebar !== VideoSidebar.EventList && sidebar !== VideoSidebar.EventsWithComments);

			const leavingCommentsSidebar = state.sidebar === VideoSidebar.EventsWithComments && sidebar !== VideoSidebar.EventsWithComments;

			let filterValues = state.filterValues;
			let filteredFilterValues = state.filteredFilterValues;
			let events = state.events;
			let mergedEvents = state.mergedEvents;
			let intervals = state.intervals;
			let lastNonCommentFilterValues = state.lastNonCommentFilterValues;

			if (leavingCommentsSidebar) {
				filterValues = state.lastNonCommentFilterValues.length ? state.lastNonCommentFilterValues : [];
				filteredFilterValues = filterValues;
				lastNonCommentFilterValues = filterValues;

				events = filterEvents(state.eventFilter, state.gameData!, filterValues);
				mergedEvents = mergeCloseTimeSpanEvents(events);
				intervals = createIntervalsFromMergedEvents(mergedEvents);

				syncSelectedEventsAfterFilterChange(mergedEvents);

				useTimeStore.setState((state) => {
					return { ...state, currentTime: 0 };
				});
			}

			const jumpBetweenIntervals = shouldJumpBetweenIntervals(
				mergedEvents,
				resolveActiveFilterValuesForSidebar(filterValues, filteredFilterValues),
				sidebar,
			);

			return {
				...state,
				sidebar,
				jumpBetweenIntervals,
				filterValues,
				filteredFilterValues,
				events,
				mergedEvents,
				intervals,
				lastNonCommentFilterValues,
				sidebarCommentEventId: null,
			};
		});
	},
	setSidebarAndFilter: (sidebar) => {
		set((state) => {
			const clearCommentPanel =
				sidebar !== VideoSidebar.EventList && sidebar !== VideoSidebar.EventsWithComments;

			let newFilterValues = state.filterValues;
			let lastNonCommentFilterValues = state.lastNonCommentFilterValues;

			const COMMENT_FILTER_KEY = "hasComments";
			const COMMENT_FILTER_VALUE = "true";

			if (sidebar === VideoSidebar.EventsWithComments) {
				if (state.sidebar !== VideoSidebar.EventsWithComments) {
					lastNonCommentFilterValues = state.filterValues;
				}
				newFilterValues = state.eventFilter.mutateFilterValues([], COMMENT_FILTER_KEY, COMMENT_FILTER_VALUE, true, true);
			} else if (state.sidebar === VideoSidebar.EventsWithComments) {
				newFilterValues = lastNonCommentFilterValues.length ? lastNonCommentFilterValues : [];
			}

			const events = filterEvents(state.eventFilter, state.gameData!, newFilterValues);
			const mergedEvents = mergeCloseTimeSpanEvents(events);
			const intervals = createIntervalsFromMergedEvents(mergedEvents);
			const jumpBetweenIntervals = shouldJumpBetweenIntervals(
				mergedEvents,
				newFilterValues,
				sidebar,
			);

			syncSelectedEventsAfterFilterChange(mergedEvents);
			useTimeStore.setState((state) => ({ ...state, currentTime: 0 }));

			return {
				...state,
				sidebar,
				filterValues: newFilterValues,
				filteredFilterValues: newFilterValues,
				lastNonCommentFilterValues,
				events,
				mergedEvents,
				intervals,
				jumpBetweenIntervals,
				sidebarCommentEventId: null,
			};
		});
	},
	setSidebarCommentEventId: (sidebarCommentEventId) => set({ sidebarCommentEventId }),
	setEvents(events, activeFilterValues) {
		const mergedEvents = mergeCloseTimeSpanEvents(events);
		const intervals = createIntervalsFromMergedEvents(mergedEvents);

		useTimeStore.setState((state) => {
			return { ...state, currentTime: 0 };
		});

		set((state) => {
			const resolvedActiveFilterValues =
				activeFilterValues ?? resolveActiveFilterValuesForSidebar(state.filterValues, state.filteredFilterValues);
			const jumpBetweenIntervals = shouldJumpBetweenIntervals(
				mergedEvents,
				resolvedActiveFilterValues,
				state.sidebar,
			);

			return { ...state, events, mergedEvents, intervals, jumpBetweenIntervals };
		});
	},
	setFollowVideo(followVideo) {
		set((state) => {
			return { ...state, followVideo };
		});
	},
	setMergedEvents(mergedEvents) {
		const intervals = createIntervalsFromMergedEvents(mergedEvents);
		set((state) => {
			return { ...state, mergedEvents, intervals };
		});
	},
});
export const useStore = create<GameState>()(
	devtools(
		// persist(store, {
		// 	name: "admin-storage",
		// }),
		store,
		{
			//@ts-ignore
			enabled: import.meta.env.DEV,
			name: "game-store",
		}
	)
);

export const useGameStore = () => useStore((state) => state);
